import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, getDramasByCategory, getCategoryName, type Drama } from '@/mocks/dramas';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'guochan');
  const [dramas, setDramas] = useState<Drama[]>([]);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [slug]);

  useEffect(() => {
    setDramas(getDramasByCategory(activeSlug));
  }, [activeSlug]);

  const categoryName = getCategoryName(activeSlug);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6 py-4 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: categoryName },
              ]}
            />
          </div>
        </div>

        <section className="px-4 md:px-6 py-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:text-foreground-900 hover:bg-background-200 border border-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="mb-6">
              <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 {dramas.length} 部{categoryName}
              </p>
            </div>

            {dramas.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {dramas.map((drama) => (
                  <Link
                    key={drama.id}
                    to={`/article/${drama.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[2/3] mb-3 bg-background-200">
                      <img
                        src={drama.coverImage}
                        alt={drama.title}
                        title={`${drama.title} - ${drama.subtitle} 高清在线观看`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                          <i className="ri-star-fill text-[10px]"></i>
                          {drama.rating}
                        </span>
                      </div>
                      {drama.isNew && (
                        <div className="absolute top-2 right-2">
                          <span className="inline-flex items-center px-2 py-0.5 bg-accent-500 text-background-50 text-[10px] font-semibold rounded-full whitespace-nowrap">
                            NEW
                          </span>
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 pt-8">
                        <span className="text-background-50 text-xs whitespace-nowrap">{drama.updateInfo}</span>
                      </div>
                    </div>
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {drama.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">
                      {drama.cast.slice(0, 3).join(' / ')}
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-movie-2-line text-4xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的电视剧</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}